import { useState, useCallback } from 'react';
import { usePlanLimits, PlanLimits } from './usePlanLimits';

interface FeatureGateConfig {
  feature: keyof PlanLimits;
  featureName: string;
  description?: string;
}

/**
 * Hook para manejar bloqueos de features con modal de upgrade integrado
 */
export function useFeatureGate() {
  const { isFeatureBlocked, isFeatureAvailable, isFree, isPaid, planInfo } = usePlanLimits();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedFeatureInfo, setBlockedFeatureInfo] = useState<{
    name: string;
    description?: string;
  } | null>(null);

  /**
   * Verifica si una feature está disponible, si no, muestra modal de upgrade
   * @returns true si la feature está disponible, false si está bloqueada
   */
  const checkFeatureAccess = useCallback((config: FeatureGateConfig): boolean => {
    const blocked = isFeatureBlocked(config.feature);
    
    if (blocked) {
      setBlockedFeatureInfo({
        name: config.featureName,
        description: config.description,
      });
      setShowUpgradeModal(true);
      return false;
    }
    
    return true;
  }, [isFeatureBlocked]);

  /**
   * Cierra el modal de upgrade
   */
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setBlockedFeatureInfo(null);
  }, []);

  /**
   * Muestra el modal de upgrade directamente
   */
  const promptUpgrade = useCallback((featureName: string, description?: string) => {
    setBlockedFeatureInfo({ name: featureName, description });
    setShowUpgradeModal(true);
  }, []);

  return {
    // Verificaciones
    isFeatureBlocked,
    isFeatureAvailable,
    isFree,
    isPaid,
    planInfo,
    
    // Modal de upgrade
    showUpgradeModal,
    blockedFeatureInfo,
    checkFeatureAccess,
    closeUpgradeModal,
    promptUpgrade,
  };
}
